import React from 'react';
import { X, Calendar, DollarSign, Package, Truck, ShoppingBag, ArrowRightLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PurchaseForm } from './PurchaseForm';
import { StockAdjustmentForm } from './StockAdjustmentForm';

interface ProductDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stockId: number | null;
  companySchema: string;
  localDestinoId: number | null;
}

interface ProductDetails {
  productName: string;
  variantName: string;
  category: string;
  sku: string;
  stockMin: number;
  stockAvailable: number;
  storeName: string;
  attributes: Record<string, string>;
  mainImage: string | null;
}

interface PurchaseHistory {
  id: number;
  fecha_compra: string;
  cantidad_comprada: number;
  costo_unitario: number;
  proveedor: string;
}

export const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  isOpen,
  onClose,
  stockId,
  companySchema,
  localDestinoId
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [details, setDetails] = React.useState<ProductDetails | null>(null);
  const [signedImageUrl, setSignedImageUrl] = React.useState<string | null>(null);
  const [showPurchaseForm, setShowPurchaseForm] = React.useState(false);
  const [showAdjustmentForm, setShowAdjustmentForm] = React.useState(false);
  const [purchaseHistory, setPurchaseHistory] = React.useState<PurchaseHistory[]>([]);
  const [stockMovements, setStockMovements] = React.useState<{
    id: number;
    tipo: string;
    cantidad: number;
    local_origen: string | null;
    local_destino: string | null;
    fecha: string;
  }[]>([]);
  const [variantId, setVariantId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (isOpen && stockId) {
      fetchProductDetails();
      fetchPurchaseHistory();
      fetchStockMovements();
    }
  }, [isOpen, stockId]);

  const fetchStockMovements = async () => {
    if (!stockId || !companySchema) return;
    
    try {
      const { data: stockData } = await supabase
        .schema(companySchema)
        .from('stock')
        .select('variantes_id')
        .eq('id', stockId)
        .single();

      if (!stockData) return;

      const { data: movements, error: movementsError } = await supabase
        .schema(companySchema)
        .from('movimiento_de_stock')
        .select(`
          id,
          tipo,
          cantidad,
          fecha,
          origen:locales!movimiento_de_stock_local_id_origen_fkey (
            nombre
          ),
          destino:locales!movimiento_de_stock_local_id_destino_fkey (
            nombre
          )
        `)
        .eq('variante_id', stockData.variantes_id)
        .or(`local_id_origen.eq.${localDestinoId},local_id_destino.eq.${localDestinoId}`)
        .order('fecha', { ascending: false });

      if (movementsError) throw movementsError;

      setStockMovements(movements.map(movement => ({
        id: movement.id,
        tipo: movement.tipo,
        cantidad: movement.cantidad,
        local_origen: movement.origen?.nombre || 'N/A',
        local_destino: movement.destino?.nombre || 'N/A',
        fecha: new Date(movement.fecha).toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      })));
    } catch (err) {
      console.error('Error fetching stock movements:', err);
    }
  };

  const fetchPurchaseHistory = async () => {
    if (!stockId || !companySchema) return;
    
    // First get the variante_id from the stock record
    try {
      const { data: stockData, error: stockError } = await supabase
        .schema(companySchema)
        .from('stock')
        .select('variantes_id')
        .eq('id', stockId)
        .single();

      if (stockError) throw stockError;
      if (!stockData) throw new Error('Stock record not found');

      const { data: purchaseData, error: queryError } = await supabase
        .schema(companySchema)
        .from('compras')
        .select(`
          id,
          fecha_compra,
          cantidad_comprada,
          costo_unitario,
          proveedores!inner (
            nombre
          )
        `)
        .eq('variante_id', stockData.variantes_id)
        .order('fecha_compra', { ascending: false });

      if (queryError) throw queryError;

      setPurchaseHistory(purchaseData?.map(item => ({
        id: item.id,
        fecha_compra: item.fecha_compra,
        cantidad_comprada: item.cantidad_comprada,
        costo_unitario: item.costo_unitario,
        proveedor: item.proveedores.nombre
      })) || []);

    } catch (err) {
      console.error('Error fetching purchase history:', err);
      // Don't set error state to avoid blocking the whole modal
    }
  };

  const fetchProductDetails = async () => {
    if (!stockId || !companySchema) return;
    
    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .schema(companySchema)
        .from('stock')
        .select(`
          id,
          cantidad_disponible,
          stock_minimo,
          local_id,
          variantes_id,
          locales!inner (
            nombre
          ),
          variantes!inner (
            id,
            nombre,
            sku,
            atributos,
            product_id,
            productos!inner (
              nombre,
              categoria_id,
              categorias!inner (
                nombre
              )
            )
          )
        `)
        .eq('id', stockId)
        .single();

      if (queryError) throw queryError;
      if (!data) throw new Error('No data found');

      setVariantId(data.variantes_id);

      // Fetch main image
      const { data: imageData } = await supabase
        .schema(companySchema)
        .from('productos_imagenes')
        .select('url')
        .eq('variante_id', data.variantes_id)
        .eq('principal', true)
        .maybeSingle(); // Changed from single() to maybeSingle()

      let signedUrl = null;
      if (imageData?.url) {
        try {
          // Get id_company/company_id for storage prefix
          const { data: auth } = await supabase.auth.getUser();
          const user = auth?.user;
          if (!user) throw new Error('No user found');
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company, company_id')
            .eq('id', user.id)
            .single();
          const idCompany = (profile as any)?.id_company ?? (profile as any)?.company_id;
          if (!idCompany) throw new Error('No company associated with user');

          // productos_imagenes.url stores filename in new system; support legacy full URL too
          const raw = imageData.url as string;
          const filename = raw.includes('/') ? raw.split('/').pop() || raw : raw;
          const optPath = `${idCompany}/${data.variantes_id}/opt/${filename}`;
          const fullPath = `${idCompany}/${data.variantes_id}/full/${filename}`;

          // Try optimized first, then full
          const { data: optSigned } = await supabase.storage.from('productos').createSignedUrl(optPath, 3600);
          if (optSigned?.signedUrl) {
            signedUrl = optSigned.signedUrl;
          } else {
            const { data: fullSigned } = await supabase.storage.from('productos').createSignedUrl(fullPath, 3600);
            if (fullSigned?.signedUrl) signedUrl = fullSigned.signedUrl;
          }
        } catch (err) {
          console.error('Error creating signed URL:', err);
          signedUrl = imageData.url;
        }
      }
      
      setSignedImageUrl(signedUrl);
      setDetails({
        productName: data.variantes.productos.nombre,
        variantName: data.variantes.nombre,
        category: data.variantes.productos.categorias.nombre,
        sku: data.variantes.sku || 'N/A',
        stockMin: data.stock_minimo,
        stockAvailable: data.cantidad_disponible,
        storeName: data.locales.nombre,
        attributes: data.variantes.atributos || {},
        mainImage: imageData?.url || null
      });

    } catch (err) {
      console.error('Error fetching product details:', err);
      setError(err instanceof Error ? err.message : 'Error loading product details');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">
            Detalles del producto
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-grow">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-red-600 text-center">{error}</div>
          ) : details ? (
            <div className="space-y-8">
              <div className="flex gap-8">
                {/* Image */}
                <div className="w-1/3 flex-shrink-0">
                  {signedImageUrl ? (
                    <img
                      src={signedImageUrl}
                      alt={details.variantName}
                      className="w-full h-auto rounded-lg shadow-sm object-cover"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400">No hay imagen</span>
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1">
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div>
                      <h4 className="text-xl font-medium text-gray-900 mb-1">
                        {details.productName}
                      </h4>
                      <p className="text-gray-500">{details.variantName}</p>
                    </div>

                    {/* Main Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          Categoría
                        </label>
                        <span className="text-gray-900">{details.category}</span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">
                          SKU
                        </label>
                        <span className="text-gray-900">{details.sku}</span>
                      </div>
                    </div>

                    {/* Stock Info */}
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Stock
                      </label>
                      <p className="text-gray-900">
                        {details.stockAvailable} unidades disponibles en el local {details.storeName}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Stock mínimo: {details.stockMin} unidades
                      </p>
                    </div>

                    {/* Attributes */}
                    {Object.keys(details.attributes).length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-3">
                          Atributos
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          {Object.entries(details.attributes).map(([key, value]) => (
                            <div key={key} className="px-3 py-2 bg-gray-50 rounded-lg text-sm">
                              <span className="font-medium text-gray-700">{key}: </span>
                              <span className="text-gray-900">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Purchase History */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    Historial de compra del producto
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowPurchaseForm(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      Comprar más unidades
                    </button>
                    <button
                      onClick={() => setShowAdjustmentForm(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Package className="w-4 h-4" />
                      Ajuste de stock
                    </button>
                  </div>
                </div>
                    
                {purchaseHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidades</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio unitario</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {purchaseHistory.map((purchase) => (
                              <tr key={purchase.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {new Date(purchase.fecha_compra).toLocaleDateString('es-ES')}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {purchase.cantidad_comprada}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  ${purchase.costo_unitario.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  ${(purchase.cantidad_comprada * purchase.costo_unitario).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {purchase.proveedor}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No hay historial de compras para este producto
                    </div>
                  )}
                  </div>
              
              {/* Stock Movements History */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5 text-gray-500" />
                    Historial de movimientos de stock
                  </h4>
                </div>
                    
                {stockMovements.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Local Origen</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Local Destino</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {stockMovements.map((movement) => (
                          <tr key={movement.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                movement.tipo === 'entrada' ? 'bg-green-100 text-green-800' :
                                movement.tipo === 'salida' ? 'bg-red-100 text-red-800' :
                                movement.tipo === 'transferencia' ? 'bg-blue-100 text-blue-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {movement.tipo}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {movement.cantidad}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {movement.local_origen}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {movement.local_destino}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {movement.fecha}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No hay historial de movimientos para este producto
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-gray-500 text-center">No se encontraron detalles</div>
          )}
        </div>

        {/* Purchase Form Modal */}
        {showPurchaseForm && variantId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <PurchaseForm
                variantId={variantId}
                companySchema={companySchema}
                localDestinoId={localDestinoId}
                onClose={() => setShowPurchaseForm(false)}
                onSuccess={() => {
                  fetchProductDetails();
                  fetchPurchaseHistory();
                }}
              />
            </div>
          </div>
        )}

        {/* Stock Adjustment Form Modal */}
        {showAdjustmentForm && variantId && details && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <StockAdjustmentForm
                variantId={variantId}
                companySchema={companySchema}
                localId={localDestinoId || 0}
                currentStock={details.stockAvailable}
                onClose={() => setShowAdjustmentForm(false)}
                onSuccess={() => {
                  fetchProductDetails();
                  fetchPurchaseHistory();
                }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};